import React, { useState } from "react";
import clsx from "clsx";
// Third Party
import moment, { Moment } from "moment";

// MUI
import TextField from "@mui/material/TextField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDateTimePicker, DesktopDateTimePickerProps, LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Paper } from "@mui/material";
import { PickersActionBarAction } from "@mui/x-date-pickers/PickersActionBar/PickersActionBar";

// Icon
import { ReactComponent as ModernCalendarIcon } from "@/assets/icons/common/modern-calendar-icon.svg";

// Styles
import "./DateTimePicker.css";

const theme = createTheme({
	palette: {
		primary: { main: "#019bd9" },
	},
	components: {
		MuiSvgIcon: {
			styleOverrides: { root: { color: "#666666" } },
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"& .MuiOutlinedInput-notchedOutline": {
						borderColor: "#CCCCCC !important",
					},
				},
			},
		},
	},
});

interface CustomTextFieldProps {
	className?: string;
	disabled?: boolean;
	InputProps: object;
	autoComplete: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onOpen?: () => void;
	onContextMenu?: React.MouseEventHandler;
}

const CustomTextField: React.FC<CustomTextFieldProps> = ({ setOpen, onOpen, onContextMenu, ...params }) => (
	<TextField
		{...params}
		fullWidth
		InputProps={{
			...params.InputProps,
			readOnly: true,
			onKeyDown: (e) => e.preventDefault(),
		}}
		onClick={() => {
			if (!params.disabled) {
				setOpen(true);
				// onOpen?.();
			}
		}}
		onContextMenu={onContextMenu}
		className={clsx("date-time-picker-input correct-shink-label", params.className)}
		autoComplete={params.autoComplete ? "on" : "off"}
	/>
);

export interface DateTimePickerControlledProps
	extends Omit<DesktopDateTimePickerProps<Date | Moment>, "onOpen" | "onAccept" | "format" | "timezone"> {
	error?: boolean;
	width?: string;
	actions?: PickersActionBarAction[];
	hideTabs?: boolean;
	showToolbar?: boolean;
	toolbarFormat?: string;
	toolbarTitle?: string;
	inputFormat?: string;
	inputProps?: Partial<{
		placeholder: string;
	}>;
	disableContextMenu?: boolean;
	allowSameDateSelection?: boolean;

	/** replaces the standard onChange: we always pass you a Moment */
	onActionUpdate: (dateTime: Moment) => void;

	/** fire when the “OK” button is hit */
	onAccept?: (date: Date | Moment | null) => void;

	/** fire when the picker opens */
	onOpen?: () => void;
}

export default function DateTimePickerControlledV2(props: DateTimePickerControlledProps) {
	const {
		width = "100%",
		inputFormat,
		inputProps,
		showToolbar = true,
		hideTabs = false,
		toolbarFormat,
		toolbarTitle,
		onActionUpdate,
		onAccept,
		onOpen,
		onClose,
		...rest
	} = props;

	const [open, setOpen] = useState(false);
	const [error, setError] = useState(false);

	/********************************************* HANDLERS ************************************************/
	const handleClose = () => {
		setOpen(false);
		onClose?.();
	};

	const handleChange = (date: Date | Moment | null) => {
		if (date !== null && !moment(date).isValid()) {
			setError(true);
			return;
		}
		if (date) onActionUpdate(moment(date));
	};

	const handleAccept = (date: Date | Moment | null) => {
		onAccept?.(date ?? null);
	};

	const handleContextMenu: React.MouseEventHandler = (e) => {
		if (props.disableContextMenu) {
			e.preventDefault();
		}
	};

	return (
		<ThemeProvider theme={theme}>
			<Paper
				elevation={0}
				className="date-time-picker-container"
				style={{
					position: "relative",
					backgroundColor: "transparent",
					width,
				}}
				sx={props.sx}
			>
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<DesktopDateTimePicker
						{...rest}
						open={open}
						className={clsx({
							"text-input-error-date-picker": props.error || error,
						})}
						slots={{
							textField: CustomTextField,
							openPickerIcon: ModernCalendarIcon,
						}}
						slotProps={{
							toolbar: {
								hidden: !props.showToolbar,
								toolbarFormat: props.toolbarFormat,
							},
							tabs: {
								hidden: props.hideTabs,
							},
							actionBar: {
								actions: props.actions,
							},
							textField: {
								inputProps: props.inputProps,
								// @ts-ignore
								setOpen,
								onOpen: props.onOpen,
								disabled: props.disabled,
								onContextMenu: handleContextMenu,
							},
						}}
						onOpen={props.onOpen}
						onClose={handleClose}
						onChange={(date) => handleChange(date)}
						onAccept={(date) => handleAccept(date)}
					/>
				</LocalizationProvider>
			</Paper>
		</ThemeProvider>
	);
}
